import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  CardElement,
  type CardElementError,
  type CardElementEvent,
  type OnBinLookupResult,
  type PayerInfo,
} from '@flywire/internal-react-elements';

import { useDispatch } from 'store/configureStore';

import {
  trackAcceptAndContinueCardSurchargePaymentWithFee,
  trackBinLookup,
  trackCardTokenizationSuccessForCardSurchargePayment,
  trackChargeResponse,
} from 'actions/tracker/tracker';
import { FLYWIRE_JS_ENVIRONMENT } from 'constants/config';
import { CARD_CHARGE_ERRORS } from 'constants/errors';
import type { RootState } from 'reducers/types';
import { getAccessToken } from 'selectors/entities/authentication/authentication';
import { getFieldByEntity } from 'selectors/entities/fields/fields';
import {
  getOrder,
  getSenderCountry,
  getSenderCurrency,
} from 'selectors/entities/order/order';
import { getFieldIdsAndValues } from 'selectors/ui/ui';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { isHttpErrorWithParsedBody } from 'utils/errors/errors';

import { cardOnFile } from 'services/cardOnFile/cardOnFile';

import { SurchargeCardFormContent } from '../SurchargeCardFormContent';
import { Error as ErrorComponent } from '../Error';
import { RedirectionModal } from '../RedirectionModal';
import { Success } from '../Success';
import { SurchargeModal } from '../SurchargeModal';
import { useCharge } from '../useCharge';
import type { ChargeError, ChargeErrorResponse } from '../useCharge';
import { useCreateCheckoutSession } from '../useCreateCheckoutSession';

type CardElementRef = {
  submit: () => Promise<void>;
};
type CardDetails = {
  cardClassification: string;
  country: string;
  productType: string;
};
type SuccessResponse = {
  confirm: {
    method: string;
    url: string;
  };
};
type Decision = {
  action: 'charge' | 'redirection' | 'surcharge';
  percentage?: number;
};
type ErrorType = ChargeError | 'session';

const SurchargeCardForm = () => {
  const ref = useRef<CardElementRef>(null);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [cardDetails, setCardDetails] = useState<CardDetails>({
    cardClassification: '',
    country: '',
    productType: '',
  });
  const [showSurchargeModal, setShowSurchargeModal] = useState(false);
  const [showRedirectionModal, setShowRedirectionModal] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [decision, setDecision] = useState<Decision | undefined>();
  const [showErrorComponent, setShowErrorComponent] = useState(false);
  const [chargeErrorType, setChargeErrorType] = useState<ErrorType>('failed');
  const [binLookupRequest, setBinLookupRequest] = useState<
    undefined | 'started' | 'finished'
  >();
  const accessToken = useSelector(getAccessToken);
  const senderCountry = useSelector(getSenderCountry);
  const currency = useSelector(getSenderCurrency);
  const order = useSelector(getOrder);

  const onChargeError = async (error: ChargeError) => {
    if (!CARD_CHARGE_ERRORS.includes(error)) {
      error = 'generic';
    }

    setChargeErrorType(error);

    setShowErrorComponent(true);
  };

  const { isLoading: isCharging, isSuccess, mutateAsync: charge } = useCharge();

  const { createSession, createSessionError, sessionId } =
    useCreateCheckoutSession({
      sessionType: 'card_tokenization_for_one_off',
    });

  useEffect(() => {
    if (createSessionError) {
      setChargeErrorType('session');
      setShowErrorComponent(true);
    }
  }, [createSessionError]);

  const { ids } = useSelector((state: RootState) =>
    getFieldByEntity(state, 'sender'),
  );
  const senderFields = useSelector((state: RootState) =>
    getFieldIdsAndValues(state, ids),
  );
  const payerInfo: Omit<PayerInfo, 'firstName' | 'lastName'> = {
    address: senderFields.sender_address1 as string,
    city: senderFields.sender_city as string,
    country: senderFields.sender_country as string,
    email: senderFields.sender_email as string,
    phone: senderFields.sender_phone as string,
    state: senderFields.sender_state as string,
    zip: (senderFields.sender_zip as string) || '',
  };

  const environment = FLYWIRE_JS_ENVIRONMENT;

  const onStartLoading = () => setIsLoading(true);

  const onEndLoading = () => setIsLoading(false);

  const onBinLookup = async (response: OnBinLookupResult) => {
    if (response.status === 'success') {
      dispatch(trackBinLookup(response));
      setCardDetails(response.result);
    } else {
      dispatch(trackBinLookup(response));
      setDecision({ action: 'charge' });
      response.status === 'failed' &&
        errorNotifier.notify(new Error('Bin Lookup service failure'));
      setBinLookupRequest('finished');
      return;
    }

    try {
      const decision = await cardOnFile.chargeDecision({
        cardClassification: response.result.cardClassification,
        cardCountry: response.result.country,
        cardProductType: response.result.productType,
        orderId: order.id,
        orderToken: order.token,
      });

      setDecision(decision);
    } catch (error) {
      setDecision({ action: 'charge' });
      errorNotifier.notify(error);
    } finally {
      setBinLookupRequest('finished');
    }
  };

  const onSubmit = async () => {
    if (!isFormValid) {
      submit();
      return;
    }

    switch (decision?.action) {
      case 'redirection':
        setShowRedirectionModal(true);
        break;
      case 'surcharge':
        setShowSurchargeModal(true);
        break;
      case 'charge':
        submit();
        break;
      default:
    }
  };

  const onSuccess = async (result: SuccessResponse) => {
    const {
      confirm: { url: confirmURL },
    } = result;

    dispatch(
      trackCardTokenizationSuccessForCardSurchargePayment({
        productType: cardDetails.productType,
      }),
    );

    try {
      const response = await charge({
        accessToken,
        confirmURL,
        cardClassification: cardDetails.cardClassification,
        cardCountry: cardDetails.country,
        cardProductType: cardDetails.productType,
        orderId: order.id,
        orderToken: order.token,
        sessionId: sessionId as string,
      });

      dispatch(trackChargeResponse(response));
    } catch (error) {
      if (isHttpErrorWithParsedBody(error)) {
        onChargeError((error as ChargeErrorResponse).parsedBody.status);
      } else {
        onChargeError('generic');
      }
    }
  };

  const onEvent = (event: CardElementEvent) => {
    if (event.type === 'form_changed') {
      setIsFormValid(event.extra.isValid);
    } else if (event.type === 'bin_lookup_started') {
      setBinLookupRequest('started');
    }
  };

  const onError = async (error: CardElementError) => {
    setIsLoading(false);

    if (error.type !== 'invalid_card_info') {
      switch (error.type) {
        case 'invalid_payer_info':
          errorNotifier.notify(
            new Error('SurchargeCardForm error: Invalid payer information'),
          );
          break;
        case 'not_found':
          errorNotifier.notify(
            new Error('SurchargeCardForm error: Session expired'),
          );
          break;
        case 'challenge_error':
          break;
        case 'internal_error':
          errorNotifier.notify(
            new Error(`SurchargeCardForm error: ${error.extra.message}`),
          );
          break;
      }

      setShowErrorComponent(true);
    }
  };

  const onErrorRetry = async () => {
    await createSession();
    setShowErrorComponent(false);
  };

  const handleSurchargeModalClose = () => setShowSurchargeModal(false);

  const handleAcceptSurcharge = async () => {
    dispatch(
      trackAcceptAndContinueCardSurchargePaymentWithFee({
        productType: cardDetails.productType,
      }),
    );
    setShowSurchargeModal(false);
    submit();
  };

  const submit = async () => await ref.current?.submit();

  return (
    <>
      <SurchargeModal
        cardProductType={cardDetails.productType}
        feeRate={decision?.percentage || 0}
        handleAcceptSurcharge={handleAcceptSurcharge}
        handleModalClose={handleSurchargeModalClose}
        show={showSurchargeModal}
      />
      <RedirectionModal
        cardProductType={cardDetails.productType}
        country={senderCountry}
        show={showRedirectionModal}
      />
      {showErrorComponent ? (
        <ErrorComponent
          cardProductType={cardDetails.productType}
          error={chargeErrorType}
          onRetry={onErrorRetry}
        />
      ) : (
        <CardElement
          currency={currency?.code}
          environment={environment}
          onEndLoading={onEndLoading}
          onError={onError}
          onEvent={onEvent}
          onStartLoading={onStartLoading}
          onSuccess={onSuccess}
          onBinLookup={onBinLookup}
          payerInfo={payerInfo}
          sessionId={sessionId as string}
          ref={ref}
        >
          <SurchargeCardFormContent
            buttonDisabled={binLookupRequest === 'started'}
            isLoading={isLoading || isCharging}
            onSubmit={onSubmit}
          />
        </CardElement>
      )}
      {isSuccess && <Success />}
    </>
  );
};

export { SurchargeCardForm };
